import { ApplicationView } from '@aha-app/mvc';
import React, { useEffect, useRef, useState } from 'react';
import { isTable } from '../objects/guards';
import { svgToIcon } from '../utils/svg';
import Tool, {
  type IToolAvailableInput,
  type IToolEnabledInput,
  type ToolButtonComponent,
} from './Tool';
import tableAlternatingColorsOffIcon from '../assets/icons/tableAlternatingRowColorsOff.svg';
import tableAlternatingColorsOnIcon from '../assets/icons/tableAlternatingRowColorsOn.svg';
import { type Instance as TippyInstance } from 'tippy.js/index';
import ToolButton from '../toolbars/ToolButton';
import { colorPaletteForObject } from '../utils/DefaultsManager';
import { ColorPalette, OpacitySlider } from './ColorSwatch';
import type Table from '../objects/Table';

export const Popup: React.FC<{
  currentColor: string;
  setCurrentColor: (color: string) => void;
  selection: Table;
  hidePopup: () => void;
  showOpacitySilder?: boolean;
  setOpacity: (opacity: number) => void;
}> = ApplicationView(
  ({ currentColor, setCurrentColor, selection, hidePopup, setOpacity }) => {
    const handleOpacityChange = (opacity: number) => {
      selection.set('alternatingRowOpacity', opacity);
      setOpacity(opacity);
    };

    const ticks = '[2, 5, 10,20,30,40,50,60,70,80,90,100]';
    const opacity = selection.alternatingRowOpacity;

    return (
      <ColorPalette
        palette={colorPaletteForObject(selection)}
        currentColor={currentColor}
        setCurrentColor={(color: string) => {
          setCurrentColor(color);
          selection.set('alternatingRowColor', color);
        }}
        hidePopup={hidePopup}
      >
        <OpacitySlider
          ticks={ticks}
          opacity={opacity}
          handleOpacityChange={handleOpacityChange}
        />
      </ColorPalette>
    );
  }
);

const fillColorButton = (): ToolButtonComponent => {
  return ApplicationView(({ selection, tool }) => {
    const tippyRef = useRef<TippyInstance>();
    if (!selection || !isTable(selection)) {
      throw new Error('This can only be applied to tables');
    }

    const fillColor = selection.alternatingRowColor;
    const initialOpacity = selection.alternatingRowOpacity;
    const [currentColor, setCurrentColor] = useState<string>(fillColor);
    const [opacity, setOpacity] = useState(initialOpacity);

    // Update color if the props change.
    useEffect(() => {
      setCurrentColor(fillColor);
    }, [fillColor]);

    return (
      <ToolButton
        popup={
          <Popup
            currentColor={currentColor}
            setCurrentColor={setCurrentColor}
            selection={selection}
            hidePopup={() => tippyRef.current?.hide()}
            showOpacitySilder={true}
            setOpacity={setOpacity}
          />
        }
        tippyRef={tippyRef}
        tool={tool}
      >
        {selection.alternatingRowColor !== 'transparent'
          ? svgToIcon(
              tableAlternatingColorsOnIcon
                .replace('fillColorPlaceholder', selection.alternatingRowColor)
                .replace('opacityPlaceholder', opacity.toString())
            )
          : svgToIcon(tableAlternatingColorsOffIcon)}
      </ToolButton>
    );
  });
};

export class ToolTableAlternatingRowColor extends Tool {
  static toolName = 'Alternating row color';

  static enabled({ selection }: IToolEnabledInput) {
    return !!selection && isTable(selection);
  }

  static available({ selection }: IToolAvailableInput) {
    return !!selection && isTable(selection);
  }

  static buttonComponent() {
    return fillColorButton();
  }
}
